import React from "react";
import { Text, VStack } from "@chakra-ui/react";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import CardHeader from "../../../components/Card/CardHeader";
import { reportDetailSectionCardProps } from "../Reports/components/ReportsCardChrome";
import { SimulatorComparisonTradeReviewPanel } from "./SimulatorComparisonTradeReviewPanel";
import { useSimulatorComparisonRuns } from "./simulatorComparisonContext";

const TextAny = Text as unknown as React.FC<any>;
const VStackAny = VStack as unknown as React.FC<any>;

export type SimulatorTradeReviewSectionProps = {
  subtleText: string;
  brokerAccountCode: string;
  reportingTimeZone: string;
  reportId?: string | null;
  reportCreatedAt?: string | null;
  prefillWindow?: {
    start_date?: string | null;
    end_date?: string | null;
    timeframe?: string | null;
  } | null;
  defaultCandleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
  simulationOverlayMode?: string;
  linkedBacktests?: import("../../../types/backtests").Backtest[];
  linkedBacktestsRefreshKey?: number;
};

/** Trade review block — subscribes to comparison runs via context, not the simulator picks/policy tree. */
function SimulatorTradeReviewSectionInner(props: SimulatorTradeReviewSectionProps): React.ReactElement | null {
  const { comparisonRuns, selectedRun } = useSimulatorComparisonRuns();

  if (comparisonRuns.length === 0) return null;

  const tradeReviewRunInProgress =
    selectedRun != null &&
    selectedRun.status === "running" &&
    selectedRun.result == null &&
    !selectedRun.error;

  const tradeReviewSubtitle = tradeReviewRunInProgress
    ? "Simulation in progress… trade review will appear when the run completes."
    : "Review executions for the comparison scope. The overlay follows the selected simulation; use the dropdown to pin another saved run.";

  return (
    <Card {...reportDetailSectionCardProps} w="100%" minW={0} mt={4} data-simulator-results-card="">
      <CardHeader pb={{ base: 3, md: 4 }}>
        <VStackAny align="stretch" spacing={2} w="100%">
          <TextAny
            fontSize={{ base: "2xl", md: "3xl" }}
            fontWeight="normal"
            color="#38BBD1"
            fontFamily="var(--font-primary)"
            lineHeight="1.15"
          >
            Trade review
          </TextAny>
          <TextAny
            data-simulator-results-description=""
            fontSize="sm"
            color={props.subtleText}
            mt={1}
            fontFamily="var(--font-primary)"
          >
            {tradeReviewSubtitle}
          </TextAny>
        </VStackAny>
      </CardHeader>
      {!tradeReviewRunInProgress ? (
        <CardBody display="flex" flexDirection="column" gap={4} pt={0} pb={4}>
          <SimulatorComparisonTradeReviewPanel
            brokerAccountCode={props.brokerAccountCode}
            reportingTimeZone={props.reportingTimeZone}
            subtleText={props.subtleText}
            reportId={props.reportId}
            reportCreatedAt={props.reportCreatedAt}
            prefillWindow={props.prefillWindow}
            defaultCandleTimeframe={props.defaultCandleTimeframe}
            comparisonRuns={comparisonRuns}
            selectedRun={selectedRun}
            simulationOverlayMode={props.simulationOverlayMode}
            linkedBacktests={props.linkedBacktests}
            linkedBacktestsRefreshKey={props.linkedBacktestsRefreshKey}
          />
        </CardBody>
      ) : null}
    </Card>
  );
}

export const SimulatorTradeReviewSection = React.memo(SimulatorTradeReviewSectionInner);
